import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, LoadingDots } from '@flos/react-ui';

import { ONE_DAY_IN_MS, START_OF_CALENDAR } from 'common/helpers/date';
import { getProductValue, productList } from 'common/helpers/product';

import Input from 'components/Input/Input';
import DatePicker from 'components/DatePicker/DatePicker';
import Select from 'components/Select/Select';

import styles from './GiftCardForm.module.css';

/* ============================================ Exported Types ============================================ */
export type TFormData = {
  type: 'add' | 'update';
  amount?: string;
  product?: string;
  startDate?: number;
  endDate?: number;
  campaign?: string;
};

/* ============================================ Local Constants ============================================ */
const initialValues: Omit<TLocalFormPageData, 'type'> = {
  amount: '',
  product: 'car',
  startDate: Date.now(),
  endDate: Date.now() + ONE_DAY_IN_MS,
  campaign: 'default'
};

/* ============================================ Local Types ============================================ */
type TLocalFormPageData = Required<TFormData>;

type TUseFormData = (initialFormData: TFormData) => {
  formData: TLocalFormPageData;
  onAmountChange: (value: string) => void;
  onProductChange: (value: string) => void;
  onStartDateChange: (value: number) => void;
  onEndDateChange: (value: number) => void;
  onCampaignChange: (value: string) => void;
};
type TRUseFormData = ReturnType<TUseFormData>;

type THeader = (props: { title: string; errorMessage?: string }) => ReactElement;

type TContent = (props: TRUseFormData & { minStartDate: Date; minEndDate: Date }) => ReactElement;

type TFooter = (props: { canSubmit: boolean; onSubmit: () => void; onCancel: () => void }) => ReactElement;

type TGiftCardForm = (props: {
  title: string;
  isLoading: boolean;
  errorMessage?: string;
  initialFormData: TFormData;
  onSubmit: (data: TLocalFormPageData) => void;
  onCancel: () => void;
}) => ReactElement;

/* ============================================ Local Functions ============================================ */
const useFormData: TUseFormData = (initialFormData) => {
  const [formData, setFormData] = useState<TLocalFormPageData>(initialValues as TLocalFormPageData);

  const handleOnAmountChange: TRUseFormData['onAmountChange'] = useCallback((value) => {
    setFormData((prevValue) => ({ ...prevValue, amount: value }));
  }, []);

  const handleOnProductChange: TRUseFormData['onProductChange'] = useCallback((value) => {
    setFormData((prevValue) => ({ ...prevValue, product: value }));
  }, []);

  const handleOnStartDateChange: TRUseFormData['onStartDateChange'] = useCallback((value) => {
    setFormData((prevValue) => ({ ...prevValue, startDate: value }));
  }, []);

  const handleOnEndDateChange: TRUseFormData['onEndDateChange'] = useCallback((value) => {
    setFormData((prevValue) => ({ ...prevValue, endDate: value }));
  }, []);

  const handleOnCampaignChange: TRUseFormData['onCampaignChange'] = useCallback((value) => {
    setFormData((prevValue) => ({ ...prevValue, campaign: value }));
  }, []);

  useEffect(() => {
    const updatedFormData = {
      type: initialFormData.type,
      amount: initialFormData.amount ?? '',
      product: initialFormData.product ?? 'car',
      startDate: initialFormData.startDate ?? Date.now(),
      endDate: initialFormData.endDate ?? Date.now() + ONE_DAY_IN_MS,
      campaign: initialFormData.campaign ?? 'default'
    };

    setFormData(updatedFormData);
  }, [initialFormData]);

  return {
    formData,
    onAmountChange: handleOnAmountChange,
    onProductChange: handleOnProductChange,
    onStartDateChange: handleOnStartDateChange,
    onEndDateChange: handleOnEndDateChange,
    onCampaignChange: handleOnCampaignChange
  };
};

/* ============================================ Local Components ============================================ */
const Header: THeader = ({ title, errorMessage }) => {
  return (
    <div className={styles['header']}>
      <div className={styles['header-title']}>{title}</div>
      {!!errorMessage ? <Box className={styles['header-error-message-container']}>{errorMessage}</Box> : null}
    </div>
  );
};

const Content: TContent = ({
  formData,
  minStartDate,
  minEndDate,
  onAmountChange,
  onProductChange,
  onStartDateChange,
  onEndDateChange,
  onCampaignChange
}) => {
  const productValue = useMemo(() => getProductValue(formData.product), [formData.product]);

  return (
    <div className={styles['content']}>
      <div className={styles['content-row']}>
        <Input
          label="Angiv gavekortbeløb (DKK)"
          value={formData.amount}
          iconShape="money-bag-kr"
          isRequired
          onChange={onAmountChange}
        />
        <Select
          contentClassName={styles['content-select']}
          label="Vælg produkt"
          value={productValue}
          options={Object.values(productList)}
          isRequired
          onChange={onProductChange}
        />
      </div>
      <div className={styles['content-row']}>
        <DatePicker
          label="Angiv startdato"
          value={formData.startDate}
          minDate={minStartDate}
          hasTime
          isRequired
          onChange={onStartDateChange}
        />
        <DatePicker
          label="Angiv slutdato"
          value={formData.endDate}
          minDate={minEndDate}
          hasTime
          isRequired
          onChange={onEndDateChange}
        />
      </div>
      <div className={styles['content-row']}>
        <Select
          label="Vælg kampagne"
          value={formData.campaign}
          options={[
            { value: 'default', label: 'Default' },
            { value: 'blackweek', label: 'Black Week' }
          ]}
          isRequired
          onChange={onCampaignChange}
        />
      </div>
    </div>
  );
};

const Footer: TFooter = ({ canSubmit, onSubmit, onCancel }) => {
  return (
    <div>
      <Button disabled={!canSubmit} theme="primary" onClick={onSubmit}>
        Udfør
      </Button>
      <Button theme="secondary" onClick={onCancel}>
        Ophæve
      </Button>
    </div>
  );
};

/* ============================================ Main Component ============================================ */
const GiftCardForm: TGiftCardForm = ({ title, isLoading, errorMessage, initialFormData, onSubmit, onCancel }) => {
  const { formData, onAmountChange, onProductChange, onStartDateChange, onEndDateChange, onCampaignChange } =
    useFormData(initialFormData);

  const canSubmit = useMemo(
    () => !isLoading && formData.amount.length > 0 && !formData.amount.match(/([^0-9]+)/g),
    [isLoading, formData.amount]
  );
  const minStartDate = START_OF_CALENDAR;
  const minEndDate = useMemo(() => new Date(formData.startDate + ONE_DAY_IN_MS), [formData.startDate]);

  const handleOnSubmit = useCallback(() => {
    onSubmit({
      type: formData.type,
      product: formData.product,
      amount: formData.amount,
      startDate: formData.startDate,
      endDate: formData.endDate,
      campaign: formData.campaign
    });
  }, [formData, onSubmit]);

  return (
    <>
      <div className={styles['root']}>
        <Header title={title} errorMessage={errorMessage} />
        <Content
          formData={formData}
          minStartDate={minStartDate}
          minEndDate={minEndDate}
          onAmountChange={onAmountChange}
          onProductChange={onProductChange}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onCampaignChange={onCampaignChange}
        />
        <Footer canSubmit={canSubmit} onSubmit={handleOnSubmit} onCancel={onCancel} />
      </div>
      {isLoading && <LoadingDots centered />}
    </>
  );
};

export default GiftCardForm;
